import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { comisionService } from '../services/comisionService';

/**
 * Lo que cambia al guardar comisiones. Incluye la distribución de cada cohorte: sin
 * eso, volver a abrir "Administrar" mostraba la de antes de guardar.
 */
function invalidarTrasGuardar(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['cohortes-comision'] });
  qc.invalidateQueries({ queryKey: ['distribucion-actual'] });
  qc.invalidateQueries({ queryKey: ['carga-comisiones'] });
  qc.invalidateQueries({ queryKey: ['instancias'] });
  qc.invalidateQueries({ queryKey: ['evaluadores'] });
  qc.invalidateQueries({ queryKey: ['evaluadores-carga'] });
}

export function useCohortesComision() {
  return useQuery({ queryKey: ['cohortes-comision'], queryFn: comisionService.cohortes });
}

export function useDistribucionActual(params: { paso: number; ano: number; semestre: number }, enabled: boolean) {
  return useQuery({
    queryKey: ['distribucion-actual', params],
    queryFn: () => comisionService.actual(params),
    enabled,
  });
}

export function useCargaComisiones(params: { paso: number; ano: number; semestre: number }) {
  return useQuery({ queryKey: ['carga-comisiones', params], queryFn: () => comisionService.carga(params) });
}

export function useDistribuirComisiones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: comisionService.distribuir,
    onSuccess: (data) => {
      // Solo al publicar cambian las comisiones y las cohortes.
      if (data.publicado) invalidarTrasGuardar(qc);
    },
  });
}

/**
 * Trae la comisión de otro paso. No invalida nada: el endpoint no escribe, devuelve
 * una propuesta para revisar. Lo que guarda es `useAplicarComisiones`.
 */
export function useCopiarComisiones() {
  return useMutation({ mutationFn: comisionService.copiar });
}

export function useAplicarComisiones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: comisionService.aplicar,
    onSuccess: () => invalidarTrasGuardar(qc),
  });
}

export function useOptimizador() {
  return useQuery({ queryKey: ['optimizador'], queryFn: comisionService.optimizador });
}

export function useGuardarOptimizador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: comisionService.guardarOptimizador,
    onSuccess: (data) => qc.setQueryData(['optimizador'], data),
  });
}
